import { useMutation } from 'react-query';
import { BillingApi } from 'app/modules/settings/modules/billing/api/billing';
import {
  SetupSubscriptionRequest,
  SubscriptionResponse,
} from 'app/modules/settings/modules/billing/types';
import { useRefreshBillingEntitlements } from 'app/modules/settings/modules/billing/hooks/useRefreshBillingEntitlements';

export type SetupSubscriptionVars = {
  payload: SetupSubscriptionRequest;
  idempotencyKey: string;
};

/**
 * `POST /billing/setup-subscription` — the trial→plan checkout.
 *
 * This is A6's exact path: it used to invalidate subscription, next-invoice,
 * payment-method, addons and plans, but neither `billingEntitlements` (which
 * decides whether an add-on renders "included in your plan" or "0 active,
 * available for purchase") nor `billingModules` / `billingUsage`. So the moment
 * a trial became a paid plan the add-ons and module chips described the trial's
 * entitlements until a manual refresh. See `invalidateBillingCaches`. It also
 * re-reads the user's permissions: a plan can grant a module's permission
 * (e.g. Analytics), and RouteAccessGuard enforces from the Redux list.
 */
export const useSetupSubscriptionMutation = () => {
  const refreshEntitlements = useRefreshBillingEntitlements();

  return useMutation<SubscriptionResponse, unknown, SetupSubscriptionVars>(
    ({ payload, idempotencyKey }) =>
      BillingApi.setupSubscription(payload, idempotencyKey),
    {
      onSuccess: () => {
        refreshEntitlements();
      },
    },
  );
};
